import React, { Fragment, useContext, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Badge } from 'reactstrap';
import { PlusCircle, Edit, Trash2, Image as ImageIcon, Clock, Loader } from 'react-feather';
import { Breadcrumbs } from '../../../../AbstractElements';
import CustomizerContext from '../../../../_helper/Customizer';
import { fetchLoadingScreenQLT, fetchUploadImageScreenQLT, fetchEditImageScreenQLT, fetchDeleteImageScreenQLT } from '../../../../api/loadingScreenQLT';
import { toast } from 'react-toastify';

const LoadingScreenQLTManagement = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [displayTime, setDisplayTime] = useState(5);

  // Load images khi component mount
  useEffect(() => {
    loadImages();
  }, []);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Hàm load danh sách loading screen QLT
  const loadImages = async () => {
    setLoading(true);
    try {
      const data = await fetchLoadingScreenQLT();
      setImages(data);
      toast.success('✅ Tải danh sách loading screen QLT thành công!');
    } catch (error) {
      toast.error('❌ Lỗi khi tải loading screen: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý chọn file
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('⚠️ Vui lòng chọn file ảnh!');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('⚠️ File ảnh quá lớn! Vui lòng chọn file nhỏ hơn 5MB.');
        return;
      }

      setSelectedFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    }
  };

  // Mở modal thêm mới
  const handleAddNew = () => {
    setEditMode(false);
    setCurrentImage(null);
    setSelectedFile(null);
    setPreviewUrl('');
    setDisplayTime(5);
    setModalOpen(true);
  };

  // Mở modal chỉnh sửa
  const handleEdit = (image) => {
    setEditMode(true);
    setCurrentImage(image);
    setPreviewUrl(image.url);
    setDisplayTime(image.time || 5);
    setSelectedFile(null);
    setModalOpen(true);
  };

  // Xử lý upload/edit image
  const handleSubmit = async () => {
    if (!selectedFile && !editMode) {
      toast.warning('⚠️ Vui lòng chọn file ảnh!');
      return;
    }

    if (displayTime <= 0 || displayTime > 60) {
      toast.warning('⚠️ Thời gian hiển thị phải từ 1-60 giây!');
      return;
    }

    setLoading(true);
    try {
      if (editMode && currentImage) {
        // Edit image
        const formData = new FormData();
        if (selectedFile) {
          formData.append('url', selectedFile);
        }
        formData.append('time', displayTime);
        
        await fetchEditImageScreenQLT(currentImage._id, formData);
        toast.success('✅ Cập nhật loading screen QLT thành công!');
      } else {
        // Upload new image
        await fetchUploadImageScreenQLT(selectedFile, displayTime);
        toast.success('✅ Thêm loading screen QLT mới thành công!');
      }
      
      setModalOpen(false);
      loadImages();
    } catch (error) {
      toast.error('❌ Lỗi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý xóa image
  const handleDelete = async (id) => {
    if (window.confirm('⚠️ Bạn có chắc chắn muốn xóa loading screen này?')) {
      setLoading(true);
      try {
        await fetchDeleteImageScreenQLT(id);
        toast.success('✅ Xóa loading screen QLT thành công!');
        loadImages();
      } catch (error) {
        toast.error('❌ Lỗi khi xóa: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Loading Screen QLT" title="Loading Screen QLT" mainTitle="Quản lý Loading Screen QLT" />
      <Container fluid={true}>
        <Row>
          <Col md="12">
            {/* Info Banner */}
            <Card className="bg-gradient-info text-white mb-4 border-0 shadow">
              <CardBody className="p-4">
                <div className="d-flex align-items-center">
                  <div className="bg-white bg-opacity-25 rounded-circle p-3 me-3">
                    <Loader size={32} />
                  </div>
                  <div>
                    <h4 className="text-white mb-1">Loading Screen QLT</h4>
                    <p className="mb-0 opacity-75">
                      Quản lý hình ảnh loading screen cho module Quản lý tài sản
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Main Content Card */}
            <Card className="shadow-sm border-0">
              <CardBody className="p-4">
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                  <div>
                    <h4 className="mb-1">
                      <ImageIcon size={24} className="me-2 text-info" />
                      Danh sách Loading Screen
                    </h4>
                    <p className="text-muted mb-0 small">
                      {images.length} loading screen đang được sử dụng
                    </p>
                  </div>
                  <Button color="info" onClick={handleAddNew} className="shadow-sm">
                    <PlusCircle size={16} className="me-2" />
                    Thêm Loading Screen
                  </Button>
                </div>

                {/* Loading State */}
                {loading && (
                  <div className="text-center my-5 py-5">
                    <div className="spinner-border text-info mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-muted">Đang tải dữ liệu...</h5>
                    <p className="text-muted small">Vui lòng chờ trong giây lát</p>
                  </div>
                )}

                {/* Empty State */}
                {!loading && images.length === 0 && (
                  <div className="text-center my-5 py-5">
                    <div className="mb-4">
                      <Loader size={100} className="text-muted opacity-50" />
                    </div>
                    <h4 className="text-muted mb-2">Chưa có loading screen QLT nào</h4>
                    <p className="text-muted mb-4">
                      Thêm loading screen đầu tiên cho module Quản lý tài sản
                    </p>
                    <Button color="info" size="lg" onClick={handleAddNew}>
                      <PlusCircle size={18} className="me-2" />
                      Thêm Loading Screen
                    </Button>
                  </div>
                )}

                {/* Images Grid */}
                {!loading && images.length > 0 && (
                  <Row>
                    {images.map((image, index) => (
                      <Col xl="3" lg="4" md="6" sm="12" key={image._id || index} className="mb-4">
                        <Card className="shadow-sm border h-100 hover-lift">
                          {/* Image Container */}
                          <div className="position-relative overflow-hidden" style={{ paddingTop: '75%', backgroundColor: '#f8f9fa' }}>
                            <img
                              src={image.url}
                              alt={`Loading Screen ${index + 1}`}
                              className="position-absolute top-0 start-0 w-100 h-100 hover-zoom"
                              style={{ objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/400x300?text=Loading+Screen';
                              }}
                            />
                            {/* Time Badge */}
                            <div className="position-absolute top-0 end-0 m-2">
                              <Badge color="dark" className="d-flex align-items-center px-3 py-2">
                                <Clock size={14} className="me-1" />
                                {image.time || 5}s
                              </Badge>
                            </div>
                            {/* Index Badge */}
                            <div className="position-absolute top-0 start-0 m-2">
                              <Badge color="info" className="px-3 py-2">
                                #{index + 1}
                              </Badge>
                            </div>
                          </div>

                          {/* Card Body */}
                          <CardBody className="bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="flex-grow-1">
                                <p className="mb-1 small text-muted">
                                  <strong>ID:</strong> {image._id?.slice(-8)}
                                </p>
                                <p className="mb-0 small">
                                  <Clock size={12} className="me-1 text-info" />
                                  Hiển thị: <strong>{image.time || 5} giây</strong>
                                </p>
                              </div>
                              <div className="btn-group">
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEdit(image)}
                                  title="Chỉnh sửa"
                                >
                                  <Edit size={14} />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDelete(image._id)}
                                  title="Xóa"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}

                {/* Statistics */}
                {!loading && images.length > 0 && (
                  <div className="mt-4 pt-4 border-top">
                    <Row className="text-center">
                      <Col md="3">
                        <div className="p-3">
                          <h2 className="text-info mb-1">{images.length}</h2>
                          <p className="text-muted mb-0 small">Tổng số loading screen</p>
                        </div>
                      </Col>
                      <Col md="3">
                        <div className="p-3">
                          <h2 className="text-success mb-1">
                            <Loader size={32} />
                          </h2>
                          <p className="text-muted mb-0 small">Module QLT</p>
                        </div>
                      </Col>
                      <Col md="3">
                        <div className="p-3">
                          <h2 className="text-warning mb-1">
                            {(images.reduce((acc, img) => acc + (img.time || 5), 0) / images.length).toFixed(1)}s
                          </h2>
                          <p className="text-muted mb-0 small">Thời gian TB</p>
                        </div>
                      </Col>
                      <Col md="3">
                        <div className="p-3">
                          <h2 className="text-primary mb-1">4:3</h2>
                          <p className="text-muted mb-0 small">Tỷ lệ khuyến nghị</p>
                        </div>
                      </Col>
                    </Row>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal Upload/Edit Loading Screen */}
        <Modal isOpen={modalOpen} toggle={handleCloseModal} size="lg" centered>
          <ModalHeader toggle={handleCloseModal} className="bg-light">
            <div className="d-flex align-items-center">
              <div className="bg-info bg-opacity-10 rounded-circle p-2 me-3">
                {editMode ? <Edit size={20} className="text-info" /> : <PlusCircle size={20} className="text-info" />}
              </div>
              <div>
                <h5 className="mb-0">
                  {editMode ? '✏️ Chỉnh sửa Loading Screen QLT' : '➕ Thêm Loading Screen QLT'}
                </h5>
                <small className="text-muted">
                  {editMode ? 'Cập nhật thông tin loading screen' : 'Upload loading screen cho module QLT'}
                </small>
              </div>
            </div>
          </ModalHeader>
          <ModalBody className="p-4">
            <FormGroup>
              <Label for="imageFile" className="fw-bold fs-6 mb-3">
                <ImageIcon size={18} className="me-2 text-info" />
                Chọn ảnh Loading Screen <span className="text-danger">*</span>
              </Label>
              <Input
                id="imageFile"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="form-control-lg"
              />
              <div className="mt-3 p-3 bg-light rounded">
                <small className="text-muted d-block mb-2">
                  <strong>📌 Yêu cầu:</strong>
                </small>
                <ul className="small text-muted mb-0 ps-3">
                  <li>Định dạng: JPG, PNG, GIF</li>
                  <li>Kích thước tối đa: 5MB</li>
                  <li>Tỷ lệ khuyến nghị: 4:3 hoặc 3:4 (cho mobile)</li>
                  <li>Hình ảnh sáng, rõ ràng, phù hợp loading screen</li>
                </ul>
              </div>
            </FormGroup>

            <FormGroup>
              <Label for="displayTime" className="fw-bold fs-6 mb-3">
                <Clock size={18} className="me-2 text-info" />
                Thời gian hiển thị (giây) <span className="text-danger">*</span>
              </Label>
              <Input
                id="displayTime"
                type="number"
                min="1"
                max="60"
                value={displayTime}
                onChange={(e) => setDisplayTime(Number(e.target.value))}
                className="form-control-lg"
                placeholder="Nhập số giây (1-60)"
              />
              <small className="text-muted d-block mt-2">
                💡 Thời gian ảnh này sẽ hiển thị trên màn hình loading (từ 1-60 giây)
              </small>
            </FormGroup>

            {/* Preview */}
            {previewUrl && (
              <div className="mt-4">
                <Label className="fw-bold fs-6 mb-3">
                  🖼️ Xem trước Loading Screen:
                </Label>
                <div className="border rounded overflow-hidden shadow-sm">
                  <div className="position-relative" style={{ paddingTop: '75%', backgroundColor: '#f8f9fa' }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="position-absolute top-0 start-0 w-100 h-100"
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                  <div className="bg-light p-3 text-center border-top">
                    <Badge color="info" className="px-3 py-2 me-2">
                      <Clock size={12} className="me-1" />
                      Hiển thị {displayTime} giây
                    </Badge>
                    <Badge color="success" className="px-3 py-2">
                      ✓ Loading Screen QLT
                    </Badge>
                  </div>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button color="secondary" onClick={handleCloseModal} disabled={loading}>
              Hủy
            </Button>
            <Button 
              color="info" 
              onClick={handleSubmit} 
              disabled={loading || (!selectedFile && !editMode)}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Đang xử lý...
                </>
              ) : (
                <>
                  {editMode ? <Edit size={16} className="me-2" /> : <PlusCircle size={16} className="me-2" />}
                  {editMode ? '💾 Cập nhật' : '➕ Thêm mới'}
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>

      {/* Custom CSS */}
      <style>{`
        .hover-lift {
          transition: all 0.3s ease;
        }
        .hover-lift:hover {
          transform: translateY(-5px);
          box-shadow: 0 1rem 3rem rgba(0,0,0,.175) !important;
        }
        .hover-zoom {
          transition: transform 0.3s ease;
        }
        .hover-lift:hover .hover-zoom {
          transform: scale(1.05);
        }
        .bg-gradient-info {
          background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
        }
      `}</style>
    </Fragment>
  );
};

export default LoadingScreenQLTManagement;

